import { ENVIRONMENT, Environment } from '../../featureFlags';

export const REGION = {
  EU: 'eu-central-1',
  US: 'us-east-1',
} as const;

export type Region = (typeof REGION)[keyof typeof REGION];

// The region of each shard, as in the environments' shard maps
// (https://internal-shardmap.quinyx.com/<environment>.json). The same in prod, rc and preprod.
const SHARD_REGION: Record<string, Region> = {
  '1': REGION.EU,
  '3': REGION.US,
};

// Environments that run in eu-central-1 only.
const EU_ONLY: readonly Environment[] = [
  ENVIRONMENT.TEST,
  ENVIRONMENT.STAGING,
  ENVIRONMENT.QDAILY,
  ENVIRONMENT.LOCALHOST,
];

/**
 * The environments a copy can go into from each environment.
 *
 * A copy into another environment is posted straight to that environment's public gateway, and
 * the target then reads the source through the source's public gateway. rc and prod accept a
 * browser request only from their own back office (CORS), and only rc and prod can be reached from
 * another environment's servers - the others are behind the VPN. So rc and prod can copy into test
 * and preprod, and every environment can copy within itself.
 */
const COPY_TARGETS: Record<Environment, readonly Environment[]> = {
  [ENVIRONMENT.PROD]: [ENVIRONMENT.PROD, ENVIRONMENT.TEST, ENVIRONMENT.PREPROD],
  [ENVIRONMENT.RC]: [ENVIRONMENT.RC, ENVIRONMENT.TEST, ENVIRONMENT.PREPROD],
  [ENVIRONMENT.TEST]: [ENVIRONMENT.TEST],
  [ENVIRONMENT.PREPROD]: [ENVIRONMENT.PREPROD],
  [ENVIRONMENT.STAGING]: [ENVIRONMENT.STAGING],
  [ENVIRONMENT.QDAILY]: [ENVIRONMENT.QDAILY],
  [ENVIRONMENT.LOCALHOST]: [ENVIRONMENT.LOCALHOST],
};

const quinyxHost = (
  prefix: 'admin' | `${Region}.web`,
  environment: Environment,
) =>
  environment === ENVIRONMENT.PROD
    ? `https://${prefix}.quinyx.com`
    : `https://${prefix}-${environment}.quinyx.com`;

export const regionOfShard = (shardId?: string | number) =>
  shardId === undefined ? undefined : SHARD_REGION[String(shardId)];

// Copies stay within the back office's region, so another environment is offered only where it
// runs in that region, and none while the region is unknown.
export const copyTargetsFrom = (
  environment: Environment,
  region: Region | undefined,
) =>
  COPY_TARGETS[environment].filter(
    (target) =>
      target === environment ||
      region === REGION.EU ||
      (region === REGION.US && !EU_ONLY.includes(target)),
  );

// The public gateway a back office in another environment posts a copy to: that environment's
// host in the region, never the plain web host. A request without a qshard cookie, as this one
// is, goes from the plain host through GlobalRouter, which puts the extapi paths it does not
// inspect on the environment's default shard - in preprod, the US one.
export const gatewayOf = (environment: Environment, region: Region) =>
  `${quinyxHost(`${region}.web`, environment)}/extapi`;

// The back office where a copy created in that environment can be followed.
export const backOfficeOf = (environment: Environment) =>
  quinyxHost('admin', environment);
